import { useEffect, useMemo, useState } from 'react'
import { fetchBacktestEvents } from '../../bot/adapters/eventFetcher'
import type { OrderData } from '../../bot/types/chartData'
import { ticketsByEntryTime } from '../../bot/utils/orderPairing'

export interface UseWalkthroughOrdersOptions {
  btid: string
  /** The run's REAL range, unix seconds — what the events are fetched over. */
  rangeStartTs: number
  rangeEndTs: number
}

export interface WalkthroughOrders {
  /** Every order event of the run, entry and exit alike — warm-up and trail included. */
  orders: OrderData[]
  /** entry time -> ticket number, for the set above. */
  tickets: ReadonlyMap<number, number>
}

/**
 * The walkthrough's order events, fetched once for the whole run and numbered.
 *
 * Lives above both readers on purpose. The trades pane and the chart's order tooltip have to
 * show the same trades under the same numbers, and neither can work that out alone: the pane
 * only ever holds what the playhead has revealed, and a marker only knows its own bar. Fetching
 * and numbering once, here, is what makes the two consistent by construction.
 *
 * The WHOLE run: with every candle on the canvas, the trades the code took during the indicator
 * warm-up and the trail are findable on the chart — their sections are tinted — so they are
 * listed and drawn like any other, and numbered from the run's first trade.
 */
export default function useWalkthroughOrders({
  btid,
  rangeStartTs,
  rangeEndTs,
}: UseWalkthroughOrdersOptions): WalkthroughOrders {
  const [orders, setOrders] = useState<OrderData[]>([])

  // The REAL range is fetched: the server pads the period with indicator warm-up and rounds it
  // to whole UTC days, and this is exactly the window its events live in.
  useEffect(() => {
    let cancelled = false
    fetchBacktestEvents(btid, rangeStartTs, rangeEndTs)
      .then((rows) => {
        if (!cancelled) setOrders(rows)
      })
      .catch(() => {
        /* fetchBacktestEvents already swallows and returns [] */
      })
    return () => {
      cancelled = true
    }
  }, [btid, rangeStartTs, rangeEndTs])

  return useMemo(() => ({ orders, tickets: ticketsByEntryTime(orders) }), [orders])
}
